/**
 * Legal Pages
 * Privacy Policy and Terms of Service
 */
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <Badge className="bg-stone-200 text-stone-700 mb-4">Legal</Badge>
          <h1 className="text-4xl font-bold text-stone-900 mb-4">Privacy Policy</h1>
          <p className="text-stone-600">Last updated: January 2025</p>
        </div>

        <Card className="mb-8">
          <CardContent className="p-8 prose prose-stone max-w-none">
            <h2 className="text-xl font-bold text-stone-900 mb-4">1. Introduction</h2>
            <p className="text-stone-700 mb-4">
              CatPals+ ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
            <p className="text-stone-700 mb-6">
              Our registered address is: 8 Harvey Lane, Norwich, Norfolk, NR70BQ, United Kingdom.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">2. Information We Collect</h2>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Personal Information</h3>
            <p className="text-stone-700 mb-4">We may collect personal information that you voluntarily provide, including:</p>
            <ul className="list-disc pl-6 text-stone-700 mb-4 space-y-1">
              <li>Name and contact information (email address, phone number, postal address)</li>
              <li>Account credentials (email and password for registered users)</li>
              <li>Payment information (processed securely through PayPal)</li>
              <li>Information about your pets (for cat sitting and microchipping services)</li>
              <li>Communications you send to us via contact forms or email</li>
            </ul>

            <h3 className="text-lg font-semibold text-stone-800 mb-2">Automatically Collected Information</h3>
            <p className="text-stone-700 mb-6">
              When you visit our website, we may automatically collect certain information including your IP address, 
              browser type, operating system, referring URLs, and information about how you interact with our website.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-stone-700 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-stone-700 mb-6 space-y-1">
              <li>Provide, maintain, and improve our services</li>
              <li>Process bookings, payments, and donations</li>
              <li>Communicate with you about appointments, orders, and enquiries</li>
              <li>Send you marketing communications (with your consent)</li>
              <li>Comply with legal obligations</li>
              <li>Protect against fraudulent or unauthorized activity</li>
            </ul>

            <h2 className="text-xl font-bold text-stone-900 mb-4">4. Information Sharing</h2>
            <p className="text-stone-700 mb-4">We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc pl-6 text-stone-700 mb-6 space-y-1">
              <li>Payment processors (PayPal) to process transactions</li>
              <li>Service providers who assist in our operations</li>
              <li>Legal authorities when required by law</li>
            </ul>

            <h2 className="text-xl font-bold text-stone-900 mb-4">5. Data Security</h2>
            <p className="text-stone-700 mb-6">
              We implement appropriate technical and organizational measures to protect your personal information. 
              However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">6. Your Rights (UK GDPR)</h2>
            <p className="text-stone-700 mb-4">Under UK data protection law, you have the right to:</p>
            <ul className="list-disc pl-6 text-stone-700 mb-6 space-y-1">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your information</li>
              <li>Object to processing of your information</li>
              <li>Request restriction of processing</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>

            <h2 className="text-xl font-bold text-stone-900 mb-4">7. Cookies</h2>
            <p className="text-stone-700 mb-6">
              We use essential cookies to ensure our website functions properly. We also use analytics cookies to understand 
              how visitors interact with our website. You can control cookie settings in your browser.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">8. Children's Privacy</h2>
            <p className="text-stone-700 mb-6">
              Our services are not directed to children under 16. We do not knowingly collect personal information from children.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">9. Contact Us</h2>
            <p className="text-stone-700 mb-4">
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <ul className="list-none text-stone-700 mb-4 space-y-1">
              <li>Email: Team@CatPals.co.uk</li>
              <li>Phone: 07754 754758</li>
              <li>Address: 8 Harvey Lane, Norwich, Norfolk, NR70BQ</li>
            </ul>

            <h2 className="text-xl font-bold text-stone-900 mb-4">10. Changes to This Policy</h2>
            <p className="text-stone-700">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const TermsPage = () => {
  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <Badge className="bg-stone-200 text-stone-700 mb-4">Legal</Badge>
          <h1 className="text-4xl font-bold text-stone-900 mb-4">Terms & Conditions</h1>
          <p className="text-stone-600">Last updated: January 2025</p>
        </div>

        <Card className="mb-8">
          <CardContent className="p-8 prose prose-stone max-w-none">
            <h2 className="text-xl font-bold text-stone-900 mb-4">1. Introduction</h2>
            <p className="text-stone-700 mb-6">
              These Terms and Conditions govern your use of the CatPals+ website and services. By accessing our website or 
              using our services, you agree to be bound by these terms.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">2. Services</h2>
            <p className="text-stone-700 mb-4">CatPals+ provides the following services:</p>
            <ul className="list-disc pl-6 text-stone-700 mb-6 space-y-1">
              <li>Cat rescue and adoption services</li>
              <li>Professional cat sitting services</li>
              <li>Microchipping services for cats and small mammals</li>
              <li>Sale of handcrafted cat-related products</li>
              <li>Acceptance of donations for cat welfare</li>
            </ul>

            <h2 className="text-xl font-bold text-stone-900 mb-4">3. Cat Sitting Services</h2>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Booking and Payment</h3>
            <ul className="list-disc pl-6 text-stone-700 mb-4 space-y-1">
              <li>Bookings must be made in advance and are subject to availability</li>
              <li>Payment is required at the time of booking via PayPal</li>
              <li>Cancellations made less than 48 hours before the booking may not be refunded</li>
            </ul>

            <h3 className="text-lg font-semibold text-stone-800 mb-2">Client Responsibilities</h3>
            <ul className="list-disc pl-6 text-stone-700 mb-6 space-y-1">
              <li>Ensure your cat is up to date with vaccinations</li>
              <li>Provide accurate information about your cat's health and behavior</li>
              <li>Provide clear access instructions and necessary supplies</li>
              <li>Inform us of any special requirements or medical conditions</li>
            </ul>

            <h2 className="text-xl font-bold text-stone-900 mb-4">4. Adoption Services</h2>
            <ul className="list-disc pl-6 text-stone-700 mb-6 space-y-1">
              <li>All cats are rehomed after a thorough application and approval process</li>
              <li>We reserve the right to refuse an adoption application</li>
              <li>Adoption fees contribute to the care and medical treatment of our rescue cats</li>
              <li>Post-adoption support is available to all adopters</li>
            </ul>

            <h2 className="text-xl font-bold text-stone-900 mb-4">5. Shop Purchases</h2>
            <ul className="list-disc pl-6 text-stone-700 mb-6 space-y-1">
              <li>All products are handcrafted with care</li>
              <li>Prices are in GBP and include VAT where applicable</li>
              <li>We aim to dispatch orders within 3-5 working days</li>
              <li>Returns are accepted within 14 days for unused items</li>
            </ul>

            <h2 className="text-xl font-bold text-stone-900 mb-4">6. Donations</h2>
            <p className="text-stone-700 mb-6">
              All donations are voluntary and non-refundable. Donations are used to support the care and welfare of our 
              rescue cats, including food, veterinary care, and shelter maintenance.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">7. Limitation of Liability</h2>
            <p className="text-stone-700 mb-6">
              While we take every precaution to ensure the safety and wellbeing of cats in our care, CatPals+ shall not be 
              liable for any indirect, incidental, or consequential damages arising from the use of our services, except 
              where required by law.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">8. Intellectual Property</h2>
            <p className="text-stone-700 mb-6">
              All content on this website, including text, images, logos, and designs, is the property of CatPals+ and 
              is protected by copyright law. You may not reproduce, distribute, or use our content without permission.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">9. Governing Law</h2>
            <p className="text-stone-700 mb-6">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes 
              shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>

            <h2 className="text-xl font-bold text-stone-900 mb-4">10. Contact</h2>
            <p className="text-stone-700 mb-4">
              For any questions regarding these Terms, please contact us:
            </p>
            <ul className="list-none text-stone-700 space-y-1">
              <li>Email: Team@CatPals.co.uk</li>
              <li>Phone: 07754 754758</li>
              <li>Address: 8 Harvey Lane, Norwich, Norfolk, NR70BQ</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export { PrivacyPolicyPage, TermsPage };
